@file:OptIn(FlowPreview::class)

package com.wintermar.inspection.core.ui.component.input

import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.input.util.InputState
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.core.ui.util.SelectionModel
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.launch

@Composable
fun InputTextAutocomplete(
    state: InputState,
    modifier: Modifier = Modifier,
    references: List<SelectionModel> = emptyList(),
    dimension: AppDimension = LocalAppTheme.current.dimension,
    color: ThemeColor = LocalAppTheme.current.color,
    onClick: (SelectionModel) -> Unit
) {
    var filteredReferences by remember { mutableStateOf<List<SelectionModel>>(emptyList()) }
    // Flag to ignore the next state change caused by the click
    var ignoreNextChange by remember { mutableStateOf(false) }
    val focusManager = LocalFocusManager.current
    val density = LocalDensity.current
    val scope = rememberCoroutineScope()
    var inputHeightPx by remember { mutableStateOf(0) }
    var inputWidthPx by remember { mutableStateOf(0) }

    LaunchedEffect(state, references) {
        snapshotFlow { state.value.text }
            .debounce(200)
            .collectLatest { query ->
                if (ignoreNextChange) {
                    ignoreNextChange = false
                    filteredReferences = emptyList()
                    return@collectLatest
                }

                // Normal filtering logic
                filteredReferences = references
                    .takeIf { query.isNotEmpty() }
                    ?.filter { it.value.contains(query, ignoreCase = true) }
                    ?.let { if (it.size == 1 && it[0].value == query) emptyList() else it.take(5) }
                    .orEmpty()
            }
    }

    LaunchedEffect(filteredReferences) {
        val query = state.value.text
        if (filteredReferences.isNotEmpty() || !(filteredReferences.isEmpty() && query.isNotEmpty()))
            return@LaunchedEffect

        val exactMatch = references.firstOrNull {
            it.value.equals(query.trim().toString(), ignoreCase = true)
        }
        if (exactMatch != null) {
            ignoreNextChange = true
            onClick(exactMatch)
        }
    }

    Column(modifier = modifier) {
        InputText(
            state,
            modifier = Modifier
                .fillMaxWidth()
                .onGloballyPositioned { coordinates ->
                    inputHeightPx = coordinates.size.height
                    inputWidthPx = coordinates.size.width
                }
        )

        Popup(
            alignment = Alignment.TopStart,
            offset = IntOffset(0, inputHeightPx),
            onDismissRequest = { filteredReferences = emptyList() },
        ) {
            // Convert pixel width to Dp and apply to popup
            val popupWidthDp = with(density) {
                if (inputWidthPx > 0) inputWidthPx.toDp() else 0.dp
            }

            Column(
                modifier = Modifier
                    .then(
                        if (popupWidthDp > 0.dp) Modifier.width(popupWidthDp) else Modifier.fillMaxWidth()
                    )
                    .animateContentSize()
                    .background(
                        color = color.surface.normal,
                        shape = RoundedCornerShape(dimension.radius.md)
                    )
                    .border(
                        width = 1.dp,
                        color = color.border.subtle,
                        shape = RoundedCornerShape(dimension.radius.md)
                    )
            ) {
                repeat(filteredReferences.size) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable {
                                ignoreNextChange = true
                                state.onSetValue(filteredReferences[it].value)
                                onClick(filteredReferences[it])
                                filteredReferences = emptyList()
                                scope.launch {
                                    delay(200)
                                    focusManager.clearFocus()
                                }
                            }
                            .padding(
                                horizontal = dimension.spacing.space3,
                                vertical = dimension.spacing.space2
                            )) {
                        TextBodySmall(text = filteredReferences[it].value)
                    }
                }
            }
        }
    }
}
